"use client"

import type React from "react"

import { useState } from "react"
import { useFieldArray, useForm } from "react-hook-form"
import { Briefcase, FileText, Loader2, Plus, X } from "lucide-react"
import { usePostCandidatesParseResume } from "openapi/api/endpoints"
import { StructuredResumeCandidate } from "openapi/api/model"
import { toast } from "sonner"

import { DialogShell } from "@/components/graphite/dialog-shell"
import { FIELD_UNDERLINE, FieldShell } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { QuietAction } from "@/components/graphite/surface"
import { cn } from "@/lib/utils"

const CONTROL = cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")
const AREA = cn(FIELD_UNDERLINE, "resize-none pb-2.5 text-[12.5px] leading-[1.7]")

export interface LinkedInFormData {
  linkedinUrl: string
}

export interface ResumeFormData {
  full_name: string
  headline?: string | null
  summary?: string | null
  skills: string[]
  location?: string | null
  current_role_title?: string | null
  source: "resume_upload"
  linkedin_profile_url?: string | null
  phone_number?: string | null
  email: string
  experiences: {
    title?: string | null
    company?: string | null
    starts_at?: string | null
    ends_at?: string | null
    location?: string | null
    description?: string | null
  }[]
}

interface AddCandidateDialogProps {
  submiResumeData: (data: ResumeFormData, resume: File) => Promise<void>
  submitLinkedInData: (data: LinkedInFormData) => Promise<void>
  submitManualData: (data: ResumeFormData) => Promise<void>
  openFromUrl?: boolean
  onClearUrlOpen?: () => void
  /**
   * How loudly the trigger reads.
   *
   * `primary` is the filled pill canvas turn 33 gives a page's one create, and
   * that is what the Candidates page's trigger is. The same dialog also opens
   * from a strip above the pipeline board, which is not a page header and does
   * not get a second pill on a screen that already has one — so the tone is the
   * caller's to state rather than this component's to assume.
   */
  triggerTone?: "primary" | "teal"
}

type AddMethod = "linkedin" | "resume" | "manual"

const createEmptyResumeFormData = (): ResumeFormData => ({
  full_name: "",
  email: "",
  headline: "",
  summary: "",
  skills: [],
  location: "",
  current_role_title: "",
  source: "resume_upload",
  linkedin_profile_url: "",
  phone_number: "",
  experiences: [],
})

export function mapResumeFormToApiCandidate(formData: ResumeFormData): StructuredResumeCandidate {
  const parseDate = (dateStr?: string | null) => {
    if (!dateStr) return null
    const [year, month, day] = dateStr.split("-").map(Number)
    if (!year || Number.isNaN(year)) return null

    return {
      year,
      month: month || undefined,
      day: day || undefined,
    }
  }

  return {
    full_name: formData.full_name || null,
    headline: formData.headline ?? null,
    summary: formData.summary ?? null,
    skills: formData.skills?.length ? formData.skills : [],
    location: formData.location ?? null,
    linkedin_profile_url:
      formData.linkedin_profile_url && formData.linkedin_profile_url.trim() !== ""
        ? formData.linkedin_profile_url.trim()
        : null,
    phone_numbers: formData.phone_number ? [formData.phone_number] : [],
    source: "resume",
    email: formData.email,
    experiences: (formData.experiences ?? []).map((exp) => ({
      title: exp.title ?? null,
      company: exp.company ?? null,
      starts_at: parseDate(exp.starts_at),
      ends_at: parseDate(exp.ends_at),
      location: exp.location ?? null,
      description: exp.description ?? null,
    })) as StructuredResumeCandidate["experiences"],
  }
}

export function mapApiCandidateToResumeForm(apiData: StructuredResumeCandidate): ResumeFormData {
  const formatDate = (d: { year?: number; month?: number; day?: number } | null): string | null => {
    if (!d || !d.year) return null
    const month = d.month ? String(d.month).padStart(2, "0") : "01"
    const day = d.day ? String(d.day).padStart(2, "0") : "01"
    return `${d.year}-${month}-${day}`
  }

  return {
    full_name: apiData.full_name ?? "",
    headline: apiData.headline ?? null,
    summary: apiData.summary ?? null,
    skills: apiData.skills ?? [],
    location: apiData.location ?? null,
    current_role_title: apiData.experiences?.[0]?.title ?? null,
    source: "resume_upload",
    email: apiData.email ?? "",
    linkedin_profile_url: apiData.linkedin_profile_url ?? null,
    phone_number: apiData.phone_numbers?.[0] ?? null,
    experiences: (apiData.experiences ?? []).map((exp) => ({
      title: exp.title ?? null,
      company: exp.company ?? null,
      starts_at: formatDate(exp.starts_at as { year?: number; month?: number; day?: number }),
      ends_at: formatDate(exp.ends_at as { year?: number; month?: number; day?: number }),
      location: exp.location ?? null,
      description: exp.description ?? null,
    })),
  }
}

export function AddCandidateDialogReusable({
  submiResumeData,
  submitLinkedInData,
  submitManualData,
  openFromUrl = false,
  triggerTone = "teal",
  onClearUrlOpen,
}: AddCandidateDialogProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [addMethod, setAddMethod] = useState<AddMethod>("linkedin")
  const [resumeFile, setResumeFile] = useState<File | null>(null)
  const [showExtractedFields, setShowExtractedFields] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const { mutateAsync: parseResume } = usePostCandidatesParseResume()

  const linkedinForm = useForm<LinkedInFormData>({
    defaultValues: {
      linkedinUrl: "",
    },
  })

  const resumeForm = useForm<ResumeFormData>({
    defaultValues: createEmptyResumeFormData(),
  })

  const {
    fields: experienceFields,
    append: appendExperience,
    remove: removeExperience,
  } = useFieldArray({
    control: resumeForm.control,
    name: "experiences",
  })

  const resetResumeState = () => {
    setResumeFile(null)
    setShowExtractedFields(false)
    setIsDragging(false)
    resumeForm.reset(createEmptyResumeFormData())
  }

  const extractDataFromResume = async (file: File): Promise<ResumeFormData | undefined> => {
    try {
      if (!file) throw new Error("File Missing")
      setIsLoading(true)
      const apiParsed = await parseResume({ data: { resume: file } })
      return mapApiCandidateToResumeForm(apiParsed)
    } catch {
      toast.error("Failed to parse file", {
        description: "Please re-upload a file.",
      })
    }
  }

  const processResumeFile = async (file: File) => {
    const validTypes = [
      ".pdf",
      ".doc",
      ".docx",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ]
    const isValidType = validTypes.some(
      (type) => file.name.toLowerCase().endsWith(type) || file.type === type,
    )

    if (!isValidType) {
      toast.error("Please upload a valid resume file (PDF, DOC, DOCX).")
      return
    }

    setResumeFile(file)
    setIsLoading(true)

    try {
      const extractedData = await extractDataFromResume(file)
      if (!extractedData) return
      resumeForm.reset(extractedData)
      setShowExtractedFields(true)
    } catch (error) {
      console.error("Error extracting resume data:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResumeUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    await processResumeFile(file)
  }

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = async (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (!file) return

    await processResumeFile(file)
  }

  const onLinkedInSubmit = async (data: LinkedInFormData) => {
    setIsLoading(true)

    try {
      await submitLinkedInData(data)
      linkedinForm.reset()
      setIsOpen(false)
      onClearUrlOpen?.()
      setAddMethod("linkedin")
    } catch (error) {
      console.error("Error adding candidate:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const onResumeSubmit = async (data: ResumeFormData) => {
    setIsLoading(true)
    if (!resumeFile) {
      toast.error("No resume file detected. Please upload a resume.")
      setIsLoading(false)
      return
    }

    try {
      await submiResumeData(data, resumeFile)
      resetResumeState()
      setAddMethod("linkedin")
      setIsOpen(false)
      onClearUrlOpen?.()
    } catch (error) {
      console.error("Error adding candidate:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const onManualSubmit = async (data: ResumeFormData) => {
    setIsLoading(true)

    try {
      await submitManualData(data)
      resetResumeState()
      setAddMethod("linkedin")
      setIsOpen(false)
      onClearUrlOpen?.()
    } catch (error) {
      console.error("Error adding candidate manually:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const validateLinkedInUrl = (url: string) => {
    const decoded = decodeURIComponent(url)
    const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9À-ÿ-]+\/?$/
    return linkedinRegex.test(decoded) || "Please enter a valid LinkedIn profile URL"
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      setAddMethod("linkedin")
      linkedinForm.reset()
      resetResumeState()
      onClearUrlOpen?.()
    }
  }

  const openManualForm = () => {
    setAddMethod("manual")
    setResumeFile(null)
    setShowExtractedFields(true)
    resumeForm.reset(createEmptyResumeFormData())
  }

  const addEmptyExperience = () =>
    appendExperience({
      title: "",
      company: "",
      starts_at: "",
      ends_at: "",
      location: "",
      description: "",
    })

  const skillsValue = resumeForm.watch("skills") ?? []

  /*
    The candidate's record, in canvas 14e's fields — underlines, not boxes.

    Every control carries its own `aria-label`: the Graphite label is a line
    of type above the field rather than a `<label for>`, so without one the
    control has no accessible name at all.
  */
  const renderCandidateDetailsFields = () => (
    <>
      <section>
        <h3 className="text-[12.5px] font-medium">Basic information</h3>
        {/* A section heading in sentence case, so it cannot be mistaken for
            the uppercase field label directly under it. */}
        <div className="mt-4 grid grid-cols-1 gap-5 @sm:grid-cols-2">
          <FieldShell
            className="@sm:col-span-2"
            label={
              <>
                Full name <span className="text-destructive">*</span>
              </>
            }
          >
            <input
              id="full_name"
              aria-label="Full name"
              {...resumeForm.register("full_name", { required: true })}
              className={CONTROL}
            />
          </FieldShell>

          <FieldShell label="Headline" className="@sm:col-span-2">
            <input
              id="headline"
              aria-label="Headline"
              placeholder="e.g., Senior Software Engineer | Full-Stack Developer"
              {...resumeForm.register("headline")}
              className={CONTROL}
            />
          </FieldShell>

          <FieldShell label="Email" className="@sm:col-span-2">
            <input
              id="email"
              type="email"
              aria-label="Email"
              placeholder="candidate@example.com"
              {...resumeForm.register("email")}
              className={CONTROL}
            />
          </FieldShell>

          <FieldShell label="Phone">
            <input
              id="phone_number"
              aria-label="Phone"
              {...resumeForm.register("phone_number")}
              className={cn(CONTROL, "font-mono")}
            />
          </FieldShell>

          <FieldShell label="LinkedIn profile">
            <input
              id="linkedin_profile_url"
              aria-label="LinkedIn profile"
              placeholder="https://www.linkedin.com/in/username"
              {...resumeForm.register("linkedin_profile_url")}
              className={CONTROL}
            />
          </FieldShell>

          <FieldShell label="Current role">
            <input
              id="current_role_title"
              aria-label="Current role"
              {...resumeForm.register("current_role_title")}
              className={CONTROL}
            />
          </FieldShell>

          <FieldShell label="Location">
            <input
              id="location"
              aria-label="Location"
              {...resumeForm.register("location")}
              className={CONTROL}
            />
          </FieldShell>
        </div>

        <FieldShell label="Summary" className="mt-5">
          <textarea
            id="summary"
            aria-label="Summary"
            rows={3}
            placeholder="Professional summary or bio"
            {...resumeForm.register("summary")}
            className={AREA}
          />
        </FieldShell>

        <FieldShell label="Skills" className="mt-5">
          <textarea
            id="skills"
            aria-label="Skills"
            rows={2}
            value={skillsValue.join(", ")}
            onChange={(event) => {
              const nextSkills = event.target.value
                .split(",")
                .map((skill) => skill.trim())
                .filter(Boolean)
              resumeForm.setValue("skills", nextSkills, { shouldDirty: true })
            }}
            placeholder="React, TypeScript, Recruiting"
            className={AREA}
          />
          {/* What the comma list parses into, as the words it will store. */}
          {skillsValue.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1.5">
              {skillsValue.map((skill, index) => (
                <span key={`${skill}-${index}`} className="text-[11.5px] text-muted-foreground">
                  {skill}
                </span>
              ))}
            </div>
          )}
        </FieldShell>
      </section>

      <section className="mt-6 border-t pt-5">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-[12.5px] font-medium">Experience</h3>
          <button
            type="button"
            onClick={addEmptyExperience}
            className="inline-flex items-center gap-1 text-[12px] font-medium text-brand-ink transition-opacity hover:opacity-80"
          >
            <Plus className="size-3.5" />
            Add experience
          </button>
        </div>

        {experienceFields.length === 0 ? (
          <p className="mt-3 flex items-center gap-2 text-[12px] text-muted-foreground">
            <Briefcase className="size-3.5 flex-none" />
            No experience yet.
          </p>
        ) : (
          /* One hairline-separated block per role, not a card apiece. */
          <div className="mt-3 divide-y border-t">
            {experienceFields.map((field, index) => (
              <div key={field.id} className="py-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-[10.5px] text-muted-foreground">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeExperience(index)}
                    aria-label={`Remove experience ${index + 1}`}
                    className="text-muted-foreground transition-colors hover:text-destructive"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>

                <div className="mt-2 grid grid-cols-1 gap-4 @sm:grid-cols-2">
                  <FieldShell label="Job title">
                    <input
                      id={`experiences.${index}.title`}
                      aria-label={`Experience ${index + 1} job title`}
                      {...resumeForm.register(`experiences.${index}.title`)}
                      className={CONTROL}
                    />
                  </FieldShell>
                  <FieldShell label="Company">
                    <input
                      id={`experiences.${index}.company`}
                      aria-label={`Experience ${index + 1} company`}
                      {...resumeForm.register(`experiences.${index}.company`)}
                      className={CONTROL}
                    />
                  </FieldShell>
                  <FieldShell label="Start">
                    <input
                      id={`experiences.${index}.starts_at`}
                      aria-label={`Experience ${index + 1} start`}
                      placeholder="YYYY-MM"
                      {...resumeForm.register(`experiences.${index}.starts_at`)}
                      className={cn(CONTROL, "font-mono")}
                    />
                  </FieldShell>
                  <FieldShell label="End">
                    <input
                      id={`experiences.${index}.ends_at`}
                      aria-label={`Experience ${index + 1} end`}
                      placeholder="YYYY-MM or leave empty for current"
                      {...resumeForm.register(`experiences.${index}.ends_at`)}
                      className={cn(CONTROL, "font-mono")}
                    />
                  </FieldShell>
                  <FieldShell label="Location" className="@sm:col-span-2">
                    <input
                      id={`experiences.${index}.location`}
                      aria-label={`Experience ${index + 1} location`}
                      {...resumeForm.register(`experiences.${index}.location`)}
                      className={CONTROL}
                    />
                  </FieldShell>
                  <FieldShell label="Description" className="@sm:col-span-2">
                    <textarea
                      id={`experiences.${index}.description`}
                      aria-label={`Experience ${index + 1} description`}
                      rows={2}
                      placeholder="Key responsibilities and achievements"
                      {...resumeForm.register(`experiences.${index}.description`)}
                      className={AREA}
                    />
                  </FieldShell>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  )

  /** The three ways in, as the sheet on a job names its sources. */
  const METHODS: { id: AddMethod; label: string }[] = [
    { id: "linkedin", label: "LinkedIn" },
    { id: "resume", label: "Résumé" },
    { id: "manual", label: "Manual" },
  ]

  const busyLabel = (idle: string) => (isLoading ? "Adding…" : idle)

  /*
    One footer for three forms. Each branch is its own <form>, so its submit is
    tied back by `form=` rather than sitting inside it — which is what lets
    Enter in a field still submit the right one.

    A résumé that has not been read yet has nothing to submit: the file is the
    action, and adding waits until there is a record to add.
  */
  const footer =
    addMethod === "resume" && !showExtractedFields ? (
      <QuietAction size="md" onClick={() => handleOpenChange(false)} disabled={isLoading}>
        Cancel
      </QuietAction>
    ) : (
      <>
        {addMethod === "resume" ? (
          <QuietAction size="md" onClick={resetResumeState} disabled={isLoading}>
            Back
          </QuietAction>
        ) : (
          <QuietAction size="md" onClick={() => handleOpenChange(false)} disabled={isLoading}>
            Cancel
          </QuietAction>
        )}
        <PageAction
          tone="primary"
          type="submit"
          form={`add-candidate-${addMethod}`}
          disabled={isLoading}
        >
          {isLoading && <Loader2 className="size-3.5 animate-spin" />}
          {busyLabel(addMethod === "manual" ? "Create Candidate" : "Add Candidate")}
        </PageAction>
      </>
    )

  return (
    <>
      <PageAction tone={triggerTone} className="text-[12.5px]" onClick={() => handleOpenChange(true)}>
        <Plus className={triggerTone === "primary" ? "size-[15px]" : "h-3.5 w-3.5"} />
        <span className="hidden @sm:inline">Add Candidate</span>
        <span className="@sm:hidden">Add</span>
      </PageAction>

      <DialogShell
        open={isOpen || openFromUrl}
        onOpenChange={handleOpenChange}
        wide
        title="Add New Candidate"
        description="From a LinkedIn profile, a résumé, or by hand."
        footer={footer}
      >
        {/*
          The three methods were bordered radio cards, one of them in a primary
          wash, which is a stock "pick a plan" pattern. The job's add sheet
          (19a–19d) names its sources as an underline strip, and this is the
          same choice, so it takes the same control.
        */}
        <div
          role="tablist"
          aria-label="How the candidate is added"
          className="flex gap-[22px] overflow-x-auto border-b"
        >
          {METHODS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={entry.id === addMethod}
              onClick={() => {
                if (entry.id === "manual") return openManualForm()
                /*
                  Entering Résumé with a manual draft goes back to the upload,
                  or the hand-typed record would show under a line saying it
                  was read from a résumé.

                  Decided by what the draft is, not by which tab was last open:
                  the Manual → LinkedIn → Résumé path arrives from LinkedIn with
                  the draft still set. A parsed résumé always has its file, and
                  a manual draft never does, so "fields but no file" is a
                  manual draft on every path.
                */
                if (entry.id === "resume" && showExtractedFields && !resumeFile) resetResumeState()
                setAddMethod(entry.id)
              }}
              className={cn(
                "-mb-px whitespace-nowrap border-b-2 px-0.5 py-2.5 text-[12.5px] transition-colors",
                entry.id === addMethod
                  ? "border-primary font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {entry.label}
            </button>
          ))}
        </div>

        {addMethod === "linkedin" && (
          <form
            id="add-candidate-linkedin"
            onSubmit={linkedinForm.handleSubmit(onLinkedInSubmit)}
            className="pt-5"
          >
            <FieldShell label="Profile URL">
              <input
                id="linkedinUrl"
                aria-label="LinkedIn profile URL"
                placeholder="https://www.linkedin.com/in/username"
                {...linkedinForm.register("linkedinUrl", {
                  required: "LinkedIn URL is required",
                  validate: validateLinkedInUrl,
                })}
                disabled={isLoading}
                className={CONTROL}
              />
              {linkedinForm.formState.errors.linkedinUrl && (
                <p className="mt-1.5 text-[11px] leading-[1.4] text-destructive">
                  {linkedinForm.formState.errors.linkedinUrl.message}
                </p>
              )}
            </FieldShell>
          </form>
        )}

        {addMethod === "resume" && (
          <div className="pt-5">
            {!showExtractedFields ? (
              <>
                {/*
                  A real `<label>`, with the file input `sr-only` inside it —
                  not an `opacity-0` input stretched over a styled box, which
                  hands the click target and the picker to the platform and
                  leaves nothing on screen that says it can be clicked. The
                  drop handlers stay on the label, so dragging a file in still
                  works.
                */}
                <label
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={cn(
                    "flex cursor-pointer flex-col items-center gap-2.5 rounded-[8px] border border-dashed px-5 py-8 text-center transition-colors",
                    isDragging ? "border-primary bg-primary/[0.06]" : "hover:bg-foreground/[0.02]",
                    isLoading && "pointer-events-none opacity-60",
                  )}
                >
                  <FileText
                    className={cn("size-6", isDragging ? "text-brand-ink" : "text-muted-foreground")}
                  />
                  <span className="text-[12.5px]">
                    {isDragging ? (
                      "Drop the résumé here"
                    ) : (
                      <>
                        Drag a résumé here, or{" "}
                        <span className="font-medium text-brand-ink">choose one</span>
                      </>
                    )}
                  </span>
                  <span className="font-mono text-[10.5px] text-muted-foreground">
                    pdf, doc, docx
                  </span>
                  {resumeFile && (
                    <span className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
                      <FileText className="size-3.5" />
                      {resumeFile.name}
                    </span>
                  )}
                  <input
                    id="resume-file"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    aria-label="Choose a résumé"
                    onChange={handleResumeUpload}
                    disabled={isLoading}
                    className="sr-only"
                  />
                </label>

                {isLoading && (
                  <p className="mt-4 flex items-center justify-center gap-2 text-[12px] text-muted-foreground">
                    <Loader2 className="size-3.5 animate-spin" />
                    Reading the résumé…
                  </p>
                )}
              </>
            ) : (
              <form
                id="add-candidate-resume"
                onSubmit={resumeForm.handleSubmit(onResumeSubmit)}
              >
                <p className="mb-5 text-[12px] leading-[1.5] text-muted-foreground">
                  <span className="font-medium text-foreground">Read from the résumé.</span>{" "}
                  Check every field before adding — nothing is saved until you do.
                </p>
                {renderCandidateDetailsFields()}
              </form>
            )}
          </div>
        )}

        {addMethod === "manual" && (
          <form
            id="add-candidate-manual"
            onSubmit={resumeForm.handleSubmit(onManualSubmit)}
            className="pt-5"
          >
            <p className="mb-5 text-[12px] leading-[1.5] text-muted-foreground">
              The same record a résumé fills in, starting empty.
            </p>
            {renderCandidateDetailsFields()}
          </form>
        )}
      </DialogShell>
    </>
  )
}
